import {
	ContentCard,
	InfoCardHeader,
	StylovyzeTableBody,
	selectSettings,
} from '@innovyze/stylovyze';
import {
	EditSensorDataSort,
	STATUS_ENUM,
	SensorDataPoint,
} from '@Types/editSensorData.types';
import React, { Dispatch, SetStateAction, useEffect } from 'react';

import { EDIT_SENSOR_DATA_HEADER } from './EditSensorDataHeader';
import EditSensorDataTableFooter from './EditSensorDataTableFooter';
import { SensorPointsFormikProps } from '@Types/sensor.types';
import { Wrapper } from './EditSensorDataTable.styles';
import editSensorDataSort from '@Pages/EditSensorData/utils/editSensorDataSort';
import editSensorRow from './editSensorRow';
import filterDuplicateSensorDataPoints from '@Utils/filterDuplicateSensorDataPoints';
import { getValue } from '@Selectors';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { useGlobalization } from '@Translations/useGlobalization';

export interface EditSensorDataTableProps {
	setConfirmModalOpen: Dispatch<SetStateAction<boolean>>;
}
const EditSensorDataTable = ({
	setConfirmModalOpen,
}: EditSensorDataTableProps) => {
	// Global hooks
	const { t } = useGlobalization();
	const { companySettings } = selectSettings();

	// selectors and formik
	const { reload, status } = useSelector(getValue).editSensorData;
	const formikProps = useFormikContext<SensorPointsFormikProps>();
	const { values, setValues } = formikProps;

	useEffect(() => {
		if (reload) {
			const points = values.points.map(el => ({
				...el,
				newValue: undefined,
				value: {
					...el.value,
					doubleValue:
						typeof el.newValue === 'number'
							? el.newValue
							: el.value.doubleValue,
				},
			})) as SensorDataPoint[];
			setValues(oldValues => ({ ...oldValues, points }));
		}
	}, [reload]);

	useEffect(() => {
		if (status === 'success') setConfirmModalOpen(true);
	}, [status]);
	const handleRequestSort = (
		_e: React.MouseEvent<unknown, MouseEvent>,
		name: string | number,
		order: 'desc' | 'asc',
	) => {
		const sort = { name, order } as EditSensorDataSort;
		setValues(oldValues => ({
			...oldValues,
			sort,
			points: editSensorDataSort(
				filterDuplicateSensorDataPoints(values.points),
				sort,
			),
		}));
	};

	return (
		<ContentCard
			applyBodyPadding={false}
			header={<InfoCardHeader>{t('Data Editor')}</InfoCardHeader>}
			footer={<EditSensorDataTableFooter />}>
			<Wrapper>
				<StylovyzeTableBody
					dataCy="dataEditorTable"
					headers={EDIT_SENSOR_DATA_HEADER.map(item => {
						switch (item.cell) {
							case 'Date/Time':
								return { ...item, cell: t('Date/Time') };
							case 'Current Value':
								return { ...item, cell: t('Current Value') };
							case 'New Value':
								return { ...item, cell: t('New Value') };
							default:
								return item;
						}
					})}
					initialized={true}
					useSkeleton={{ rowCount: 5 }}
					sort={{
						...values?.sort,
						onRequestSort: handleRequestSort,
					}}
					rows={values?.points?.map((point, index) =>
						editSensorRow({
							point,
							index,
							companySettings,
							loading:
								status === STATUS_ENUM.SUCCESS ||
								status === STATUS_ENUM.PENDING,
							formikProps,
						}),
					)}
				/>
			</Wrapper>
		</ContentCard>
	);
};

export default EditSensorDataTable;
